import { operators as o } from '@pendo/aggregations';
import { buildPrimaryKeyIdColumnLabel } from '@/reports/funnel/funnel-primary-key-options.helper';
import { PRODUCT_AREAS, SUB_PRODUCT_AREAS } from '@/utils/aggregation';

type Resources = Record<'name', string>;

type FunnelParams = {
    resources: Array<Resources>;
    gcsFileName: string;
    // Pre-resolved singular noun for the funnel's primary key (defaults to
    // "Visitor"). Drives the CSV column label so an object-keyed funnel
    // exports e.g. "Report ID" instead of "Visitor ID".
    primaryKeySingular?: string;
};

export default {
    name: 'funnel-csv',
    productArea: PRODUCT_AREAS.ANALYTICS,
    subProductArea: SUB_PRODUCT_AREAS.FUNNELS,
    response: ({ resources, gcsFileName, primaryKeySingular }: FunnelParams) => ({
        location: 'gcs',
        mimeType: 'text/csv',
        name: `${gcsFileName}.csv`,
        fields: [
            { field: 'primaryKeyValue', label: buildPrimaryKeyIdColumnLabel(primaryKeySingular || 'Visitor') },
            { field: 'steps', label: '# Steps completed' },
            ...resources.map((resource, i) => ({
                field: `step${i + 1}`,
                label: `Timestamp Step ${i + 1}: ${resource.name}`,
            })),
        ],
    }),
    build: ({ gcsFileName, resources }: FunnelParams) => {
        return o.pipeline(
            {
                source: {
                    gcs: gcsFileName,
                },
            },
            // Cached GCS snapshots from before the visitorId → primaryKeyValue
            // rename still carry the identifier under `visitorId`; project it
            // forward so the filter/select below work against either schema.
            // Remove once cached pre-rename runs have aged out of the 14-day
            // funnel cache window.
            o.evalExpression({
                primaryKeyValue: 'if(primaryKeyValue == null, visitorId, primaryKeyValue)',
            }),
            o.filter('!isNil(primaryKeyValue)'),
            o.select({
                primaryKeyValue: 'primaryKeyValue',
                steps: 'steps',
                ...resources.reduce((map: Record<string, string>, _resource, i) => {
                    map[`step${i + 1}`] = `formatTime("2006-01-02 15:04:05",times[${i}])`;

                    return map;
                }, {}),
            }),
            o.sort('primaryKeyValue'),
        );
    },
};
